/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AirHandlerTypeType,
  ApplianceItemType,
  AttributeFollowUpType,
  AttributeType,
  BTUMultiplierType,
  ChatbotConversationCategoryType,
  ChatbotSubAgentContextOptionType,
  ChatbotSubAgentToolType,
  ChatbotSubAgentType,
  FenestrationItemType,
  ItemType,
  OrchestratorModelType,
  OrchestratorProviderType,
  OrchestratorTaskModelType,
  PromptType,
  ScoreDefinitionAdminType,
  ScoringParameterAdminType,
  ScoringScenarioAdminType,
  SettingsType,
  StructureSubTypeRuleAdminType,
  StructureTypeType,
  SystemDesignRuleType,
  SystemSelectorCustomerType,
  TextKeyType,
  ValidItemsType
} from 'data/types';

export interface AdminState {
  airHandlerTypes: AirHandlerTypeType[];
  btuMultipliers: BTUMultiplierType[];
  items: ItemType[];
  attributes: AttributeType[];
  attributeFollowUps: AttributeFollowUpType[];
  structureTypes: StructureTypeType[];
  validItems: ValidItemsType[];
  fenestration: FenestrationItemType[];
  applianceItems: ApplianceItemType[];
  prompts: PromptType[];
  designRules: SystemDesignRuleType[];
  designRuleStepOptions: string[];
  designRuleRecommendationTypes: { id: number; code: string; label: string }[];
  textKeys: TextKeyType[];
  insulationLevelSettings: Record<
    string,
    Record<number, { primaryValue: string; secondaryValue: string | null }>
  > | null;
  orchestratorProviders: OrchestratorProviderType[];
  orchestratorModels: OrchestratorModelType[];
  orchestratorTaskModels: OrchestratorTaskModelType[];
  chatbotSubAgents: ChatbotSubAgentType[];
  chatbotSubAgentTools: ChatbotSubAgentToolType[];
  chatbotConversationCategories: ChatbotConversationCategoryType[];
  chatbotContextOptions: ChatbotSubAgentContextOptionType[];
  structureSubTypeRules: StructureSubTypeRuleAdminType[];
  scoringScenarios: ScoringScenarioAdminType[];
  scoringParameters: ScoringParameterAdminType[];
  scoreDefinitions: ScoreDefinitionAdminType[];
  systemSelectorCustomers: SystemSelectorCustomerType[];
  mcpToolNames: string[];
  settings: SettingsType | null;
  loading: boolean;
}

export const defaultAdminState: AdminState = {
  airHandlerTypes: [],
  btuMultipliers: [],
  items: [],
  attributes: [],
  attributeFollowUps: [],
  structureTypes: [],
  validItems: [],
  fenestration: [],
  applianceItems: [],
  prompts: [],
  designRules: [],
  designRuleStepOptions: [],
  designRuleRecommendationTypes: [],
  textKeys: [],
  insulationLevelSettings: null,
  orchestratorProviders: [],
  orchestratorModels: [],
  orchestratorTaskModels: [],
  chatbotSubAgents: [],
  chatbotSubAgentTools: [],
  chatbotConversationCategories: [],
  chatbotContextOptions: [],
  structureSubTypeRules: [],
  scoringScenarios: [],
  scoringParameters: [],
  scoreDefinitions: [],
  systemSelectorCustomers: [],
  mcpToolNames: [],
  settings: null,
  loading: true
};

export const adminSlice = createSlice({
  name: 'admin',
  initialState: defaultAdminState,
  reducers: {
    setAirHandlerTypes: (
      state,
      action: PayloadAction<AirHandlerTypeType[]>
    ) => {
      state.airHandlerTypes = action.payload;
    },
    setBtuMultipliers: (state, action: PayloadAction<BTUMultiplierType[]>) => {
      state.btuMultipliers = action.payload;
    },
    setItems: (state, action: PayloadAction<ItemType[]>) => {
      state.items = action.payload;
    },
    setAttributes: (state, action: PayloadAction<AttributeType[]>) => {
      state.attributes = action.payload;
    },
    setAttributeFollowUps: (
      state,
      action: PayloadAction<AttributeFollowUpType[]>
    ) => {
      state.attributeFollowUps = action.payload;
    },
    setStructureTypes: (state, action: PayloadAction<StructureTypeType[]>) => {
      state.structureTypes = action.payload;
    },
    setValidItems: (state, action: PayloadAction<ValidItemsType[]>) => {
      state.validItems = action.payload;
    },
    setFenestration: (state, action: PayloadAction<FenestrationItemType[]>) => {
      state.fenestration = action.payload;
    },
    setApplianceItems: (state, action: PayloadAction<ApplianceItemType[]>) => {
      state.applianceItems = action.payload;
    },
    setPrompts: (state, action: PayloadAction<PromptType[]>) => {
      state.prompts = action.payload;
    },
    setDesignRules: (state, action: PayloadAction<SystemDesignRuleType[]>) => {
      state.designRules = action.payload;
    },
    setDesignRuleStepOptions: (state, action: PayloadAction<string[]>) => {
      state.designRuleStepOptions = action.payload;
    },
    setDesignRuleRecommendationTypes: (
      state,
      action: PayloadAction<{ id: number; code: string; label: string }[]>
    ) => {
      state.designRuleRecommendationTypes = action.payload;
    },
    setTextKeys: (state, action: PayloadAction<TextKeyType[]>) => {
      state.textKeys = action.payload;
    },
    setInsulationLevelSettings: (
      state,
      action: PayloadAction<Record<
        string,
        Record<number, { primaryValue: string; secondaryValue: string | null }>
      > | null>
    ) => {
      state.insulationLevelSettings = action.payload;
    },
    setOrchestratorProviders: (
      state,
      action: PayloadAction<OrchestratorProviderType[]>
    ) => {
      state.orchestratorProviders = action.payload;
    },
    setOrchestratorModels: (
      state,
      action: PayloadAction<OrchestratorModelType[]>
    ) => {
      state.orchestratorModels = action.payload;
    },
    setOrchestratorTaskModels: (
      state,
      action: PayloadAction<OrchestratorTaskModelType[]>
    ) => {
      state.orchestratorTaskModels = action.payload;
    },
    setChatbotSubAgents: (
      state,
      action: PayloadAction<ChatbotSubAgentType[]>
    ) => {
      state.chatbotSubAgents = action.payload;
    },
    setChatbotSubAgentTools: (
      state,
      action: PayloadAction<ChatbotSubAgentToolType[]>
    ) => {
      state.chatbotSubAgentTools = action.payload;
    },
    setChatbotConversationCategories: (
      state,
      action: PayloadAction<ChatbotConversationCategoryType[]>
    ) => {
      state.chatbotConversationCategories = action.payload;
    },
    setChatbotContextOptions: (
      state,
      action: PayloadAction<ChatbotSubAgentContextOptionType[]>
    ) => {
      state.chatbotContextOptions = action.payload;
    },
    setStructureSubTypeRules: (
      state,
      action: PayloadAction<StructureSubTypeRuleAdminType[]>
    ) => {
      state.structureSubTypeRules = action.payload;
    },
    setScoringScenarios: (
      state,
      action: PayloadAction<ScoringScenarioAdminType[]>
    ) => {
      state.scoringScenarios = action.payload;
    },
    setScoringParameters: (
      state,
      action: PayloadAction<ScoringParameterAdminType[]>
    ) => {
      state.scoringParameters = action.payload;
    },
    setScoreDefinitions: (
      state,
      action: PayloadAction<ScoreDefinitionAdminType[]>
    ) => {
      state.scoreDefinitions = action.payload;
    },
    setSystemSelectorCustomers: (
      state,
      action: PayloadAction<SystemSelectorCustomerType[]>
    ) => {
      state.systemSelectorCustomers = action.payload;
    },
    setMcpToolNames: (state, action: PayloadAction<string[]>) => {
      state.mcpToolNames = action.payload;
    },
    setSettings: (state, action: PayloadAction<SettingsType | null>) => {
      state.settings = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    }
  }
});

export const {
  setAirHandlerTypes,
  setBtuMultipliers,
  setItems,
  setAttributes,
  setAttributeFollowUps,
  setStructureTypes,
  setValidItems,
  setFenestration,
  setApplianceItems,
  setPrompts,
  setDesignRules,
  setDesignRuleStepOptions,
  setDesignRuleRecommendationTypes,
  setTextKeys,
  setInsulationLevelSettings,
  setOrchestratorProviders,
  setOrchestratorModels,
  setOrchestratorTaskModels,
  setChatbotSubAgents,
  setChatbotSubAgentTools,
  setChatbotConversationCategories,
  setChatbotContextOptions,
  setStructureSubTypeRules,
  setScoringScenarios,
  setScoringParameters,
  setScoreDefinitions,
  setSystemSelectorCustomers,
  setMcpToolNames,
  setSettings,
  setLoading
} = adminSlice.actions;

export default adminSlice.reducer;
